import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { v4 } from 'uuid';

import { StateService } from '../../core/state.service';
import { UploadService } from '../../core/upload.service';
import { ApiHelperService } from '../../core/api-helper.service';
import { ErrorContext, ServerError } from '../errors/error.model';
import { Organization } from '../organization/organization.model';
import { User } from '../user/user.model';
import { Statement, StatementStatusEnum } from './statement.model';
import { Merchant } from '../merchant/merchant.model';

export type FullStatement = {
    statement: Statement;
    merchant: Merchant;
    owner: User;
    organization: Organization;
};

class StatementUploadError extends ServerError {
    constructor(msg: string, ctx?: ErrorContext) {
        super(msg, ctx);

        this.ctx.userHeader = 'Internal Error';
        this.ctx.userMessage = 'We were unable to submit the statement. Support has been notified.';
    }
}

@Injectable({
    providedIn: 'root'
})
export class StatementService {

    private options = { authenticate: true, setRoot: true };

    constructor(
        private api: ApiHelperService,
        private state: StateService,
        private upload: UploadService,
    ) { }

    async getMerchantStatements(merchantId: string): Promise<Statement[]> {
        const user: User | undefined = await this.state.getUser();
        const query: Partial<Statement> = { merchantId };
        if (!user || !user.isAdmin)
            query.isDeleted = false;

        return this.api.apiCallSane(
            '/statements',
            'GET',
            this.api.getDefaultHeaders(),
            query,
            null,
            this.options)
            .then((statements: Partial<Statement>[]) => statements.map(s => new Statement(s)));

    }

    async getOrganizationStatements(organizationId: string): Promise<Statement[]> {
        const user: User | undefined = await this.state.getUser();
        const query: Partial<Statement> = { organizationId };
        if (!user || !user.isAdmin)
            query.isDeleted = false;

        return this.api.apiCallSane(
            '/statements',
            'GET',
            this.api.getDefaultHeaders(),
            query,
            null,
            this.options)
            .then((statements: Partial<Statement>[]) => statements.map(s => new Statement(s)));

    }

    getCompletedMerchantStatements(merchantId: string): Promise<Statement[]> {
        return this.api.apiCallSane(
            '/statements',
            'GET',
            this.api.getDefaultHeaders(),
            { isDeleted: false, merchantId, status: StatementStatusEnum.COMPLETED },
            null,
            this.options)
            .then((statements: Partial<Statement>[]) => statements.map(s => new Statement(s)));
    }

    getStatementById(id: string): Promise<Statement> {
        return this.api.apiCallSane(
            '/statement',
            'GET',
            this.api.getDefaultHeaders(),
            { id },
            null,
            this.options)
            .then((statement: Partial<Statement>) => new Statement(statement));
    }

    getStatementByMerchantIdAndStatementId(merchantId: string, statementId: string): Promise<FullStatement> {
        return this.api.apiCallSane(
            '/anon/statement',
            'GET',
            this.api.getDefaultHeaders(),
            { merchantId, statementId },
            null,
            this.options);
    }

    getAdminReviewStmts(): Promise<Statement[]> {
        return this.api.apiCallSane(
            '/statements',
            'GET',
            this.api.getDefaultHeaders(),
            { status: StatementStatusEnum.ADMIN_REVIEW },
            null,
            this.options)
            .then((statements: Partial<Statement>[]) => statements.map(s => new Statement(s)));
    }

    getOrgUserReviewStmts(organizationId: string): Promise<Statement[]> {
        return this.api.apiCallSane(
            '/statements',
            'GET',
            this.api.getDefaultHeaders(),
            { isDeleted: false, status: StatementStatusEnum.USER_REVIEW, organizationId },
            null,
            this.options)
            .then((statements: Partial<Statement>[]) => statements.map(s => new Statement(s)));
    }

    deleteStatement(id: string): Promise<Statement> {
        return this.api.apiCallSane('/statement',
            'PATCH',
            this.api.getDefaultHeaders(),
            { id },
            {
                // TODO (jacob): Why is this duplicated?
                isDeleted: true,
                status: StatementStatusEnum.DELETED
            },
            this.options
        );
    }

    addStatement(
        user: User,
        organizationId: string,
        organizationName: string,
        merchantId: string,
        merchantName: string,
        fileToUpload: File,
        fileName): Promise<{ statement: Statement | null, organization: Organization | null }> {
        const statementId: string = v4();
        return this.upload.uploadStatement(
            fileToUpload,
            organizationName,
            merchantId,
            statementId,
            user)
            .then((statement: Statement) => {
                statement.id = statementId;
                statement.merchantId = merchantId;
                statement.friendlyName = fileName;
                statement.fileName = fileName;
                statement.merchantName = merchantName;
                statement.createdBy = user.id!;
                return this.api.apiCallSane(
                    '/statement',
                    'POST',
                    this.api.getDefaultHeaders(),
                    null,
                    // TODO (jacob): hack. fix the pdf-server
                    Object.keys(statement).reduce((acc, key) => {
                        if (statement[key] != null)
                            acc[key] = statement[key];

                        return acc;
                    }, {}),
                    this.options)
                    .then(ret => ret ?? {})
                    .catch(e => {
                        throw StatementUploadError.upgrade(e);
                    });
            });
    }

    // same flow as addStatement, but without a pdf. Should only be used for blank statement flow
    async addStatementWithoutPdf(
        statement: Statement | Partial<Statement>,
    ): Promise<{ statement: Statement | null, organization: Organization | null }> {
        const sessionUser = await this.state.getUser();
        if (sessionUser?.id) {
            statement.createdBy = sessionUser.id;
        }
        return this.api.apiCallSane(
            '/statement',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            // TODO (jacob): hack. fix the pdf-server
            Object.keys(statement).reduce((acc, key) => {
                if (statement[key] != null)
                    acc[key] = statement[key];

                return acc;
            }, {}),
            this.options)
            .then(ret => ret ?? {})
            .catch(e => {
                throw StatementUploadError.upgrade(e);
            });
    }

    update(id: string, statement: Partial<Statement>): Promise<Statement> {
        return this.api.apiCallSane(
            '/statement',
            'PATCH',
            this.api.getDefaultHeaders(),
            { id },
            statement,
            this.options)
            .then((statement: Partial<Statement>) => new Statement(statement));
    }

    sendProcessingCompletedEmail(merchantId, statementId): Promise<void> {
        const body = {
            merchantId,
            statementId,
        };

        return this.api.apiCallSane(
            '/statement/processing-complete',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            body,
            this.options
        );
    }

    sendToAdminReview(id, isFreeTrialAccount = false):
        Promise<{ organization: Organization | null, statement: Statement } | null> {
        const headers: HttpHeaders = this.api.getDefaultHeaders();
        return this.api.apiCallSane(
            '/agg/send-statement-to-admin-review',
            'PUT',
            headers,
            { id },
            { isFreeTrialAccount },
            this.options);
    }
}
